import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, MapPin, Phone, Mail, Warehouse, Send, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { WorkshopAccountsDialog } from '../components/leasing/WorkshopAccountsDialog';

const EMPTY = { name: '', address: '', city: '', phone: '', email: '', specialty: '', notes: '' };

const WorkshopDialog = ({ open, onOpenChange, workshop, el, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(workshop ? { ...EMPTY, ...workshop } : EMPTY); }, [open, workshop]);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const field = (k, label, props = {}) => (
        <div className="space-y-1"><Label>{label}</Label><Input value={form[k] || ''} onChange={set(k)} data-testid={`workshop-${k}`} {...props} /></div>
    );

    const save = async () => {
        setSaving(true);
        try {
            const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] || '']));
            if (workshop) await api.put(`/leasing/workshops/${workshop.id}`, body);
            else await api.post('/leasing/workshops', body);
            toast.success(el ? 'Αποθηκεύτηκε' : 'Saved');
            onOpenChange(false);
            onSaved();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-lg" data-testid="workshop-dialog">
                <DialogHeader><DialogTitle>{workshop ? (el ? 'Επεξεργασία συνεργείου' : 'Edit workshop') : (el ? 'Νέο συνεργείο' : 'New workshop')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    {field('name', el ? 'Επωνυμία' : 'Name')}
                    <div className="grid grid-cols-2 gap-3">{field('address', el ? 'Διεύθυνση' : 'Address')}{field('city', el ? 'Πόλη' : 'City')}</div>
                    <div className="grid grid-cols-2 gap-3">{field('phone', el ? 'Τηλέφωνο' : 'Phone')}{field('email', 'Email', { type: 'email' })}</div>
                    {field('specialty', el ? 'Ειδικότητα (π.χ. Service, Ελαστικά, Φανοποιία)' : 'Specialty')}
                    <div className="space-y-1"><Label>{el ? 'Σημειώσεις' : 'Notes'}</Label><Textarea value={form.notes || ''} onChange={set('notes')} data-testid="workshop-notes" /></div>
                    <Button onClick={save} disabled={saving || !form.name?.trim()} className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="workshop-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποθήκευση' : 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const InviteDialog = ({ workshop, el, onClose, onSent }) => {
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);
    useEffect(() => { setEmail(workshop?.email || ''); }, [workshop]);
    const send = async () => {
        setSaving(true);
        try {
            await api.post(`/leasing/workshops/${workshop.id}/invite`, { email: email.trim() });
            toast.success(el ? 'Η πρόσκληση στάλθηκε' : 'Invite sent');
            onClose();
            onSent();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open={Boolean(workshop)} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-white max-w-md" data-testid="workshop-invite-dialog">
                <DialogHeader><DialogTitle>{el ? `Πρόσκληση: ${workshop?.name || ''}` : `Invite: ${workshop?.name || ''}`}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <p className="text-sm text-slate-500">{el ? 'Το συνεργείο θα λάβει email. Μόλις εγγραφεί (ή συνδεθεί) στο Cofleet ως Συνεργείο με αυτό το email και αποδεχτεί, θα βλέπει τα ραντεβού σας στον δικό του λογαριασμό.' : 'The workshop gets an email. Once it signs up (or logs in) as a Workshop with this email and accepts, it sees your appointments in its own account.'}</p>
                    <div className="space-y-1"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="workshop-invite-email" /></div>
                    <Button onClick={send} disabled={saving || !email.trim()} className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="workshop-invite-send">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποστολή πρόσκλησης' : 'Send invite')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const LinkStatus = ({ w, invite, el }) => {
    if (w.linked_company_id) return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-sm flex items-center gap-1" data-testid={`workshop-linked-${w.id}`}><Link2 className="w-3 h-3" />{el ? 'Συνδεδεμένο στο Cofleet' : 'Linked to Cofleet'}</span>;
    if (invite) return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-sm" data-testid={`workshop-invite-pending-${w.id}`}>{el ? `Πρόσκληση σε αναμονή (${invite.email})` : `Invite pending (${invite.email})`}</span>;
    return null;
};

const WorkshopCard = ({ w, el, onEdit, onDelete, isAdmin, invite, onInvite, onUnlink }) => (
    <div className="fleet-card p-5 flex items-start gap-4" data-testid={`workshop-card-${w.id}`}>
        <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-sm flex items-center justify-center flex-shrink-0"><Warehouse className="w-5 h-5 text-[#1B7EB5]" /></div>
        <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-slate-800">{w.name}</p><LinkStatus w={w} invite={invite} el={el} /></div>
            {w.specialty && <p className="text-xs text-slate-500">{w.specialty}</p>}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                {(w.address || w.city) && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{[w.address, w.city].filter(Boolean).join(', ')}</span>}
                {w.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{w.phone}</span>}
                {w.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{w.email}</span>}
            </div>
            {w.notes && <p className="text-xs text-slate-500 mt-2">{w.notes}</p>}
        </div>
        <div className="flex gap-1">
            {isAdmin && !w.linked_company_id && <Button variant="ghost" size="sm" onClick={onInvite} className="text-teal-700" title={el ? 'Πρόσκληση στο Cofleet' : 'Invite to Cofleet'} data-testid={`workshop-invite-${w.id}`}><Send className="w-4 h-4" /></Button>}
            {isAdmin && w.linked_company_id && <Button variant="ghost" size="sm" onClick={onUnlink} className="text-slate-500" title={el ? 'Αποσύνδεση' : 'Unlink'} data-testid={`workshop-unlink-${w.id}`}><Unlink className="w-4 h-4" /></Button>}
            {isAdmin && !w.linked_company_id && <WorkshopAccountsDialog workshop={w} el={el} />}
            <Button variant="ghost" size="sm" onClick={onEdit} className="text-[#1B7EB5]" data-testid={`workshop-edit-${w.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500" data-testid={`workshop-delete-${w.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
    </div>
);

export default function LeasingWorkshopsPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const [dialog, setDialog] = useState({ open: false, workshop: null });
    const [invites, setInvites] = useState({});
    const [inviting, setInviting] = useState(null);

    const load = useCallback(() => {
        api.get('/leasing/workshops').then((r) => setItems(r.data)).catch(() => setItems([]));
        api.get('/leasing/workshop-invites').then((r) => setInvites(Object.fromEntries(r.data.map((i) => [i.leasing_workshop_id, i])))).catch(() => setInvites({}));
    }, []);
    const unlink = (w) => window.confirm(el ? `Αποσύνδεση του ${w.name} από τον λογαριασμό Cofleet του;` : `Unlink ${w.name}?`) &&
        api.post(`/leasing/workshops/${w.id}/unlink`).then(load).catch((e) => toast.error(e.response?.data?.detail || 'Error'));
    useEffect(() => { load(); }, [load]);

    const remove = async (w) => {
        if (!window.confirm(el ? `Διαγραφή του ${w.name};` : `Delete ${w.name}?`)) return;
        try {
            await api.delete(`/leasing/workshops/${w.id}`);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    return (
        <div className="space-y-6" data-testid="leasing-workshops-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{el ? 'Συνεργεία' : 'Workshops'}</h1>
                    <p className="text-zinc-500">{el ? 'Συνεργαζόμενα συνεργεία για τις συντηρήσεις των οχημάτων leasing' : 'Partner garages for leased vehicle maintenance'}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, workshop: null })} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="add-workshop-btn"><Plus className="w-4 h-4 mr-2" />{el ? 'Νέο συνεργείο' : 'New workshop'}</Button>
            </div>
            {items === null ? <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" /> : items.length ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {items.map((w) => <WorkshopCard key={w.id} w={w} el={el} onEdit={() => setDialog({ open: true, workshop: w })} onDelete={() => remove(w)} isAdmin={user?.role === 'admin'} invite={invites[w.id]} onInvite={() => setInviting(w)} onUnlink={() => unlink(w)} />)}
                </div>
            ) : (
                <div className="fleet-card p-10 text-center text-slate-500" data-testid="workshops-empty">{el ? 'Δεν υπάρχουν συνεργεία ακόμα.' : 'No workshops yet.'}</div>
            )}
            <InviteDialog workshop={inviting} el={el} onClose={() => setInviting(null)} onSent={load} />
            <WorkshopDialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })} workshop={dialog.workshop} el={el} onSaved={load} />
        </div>
    );
}
